import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { jwtDecode } from "jwt-decode";
import * as XLSX from "xlsx";

import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@mui/icons-material/GetApp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Tooltip,
  Alert,
  IconButton,
  Typography,
} from "@mui/material";

import API_BASE_URL from "../../config/api";
import { COLORS } from "../UserDashboard/dashboardConfig";
import { usePageStyles } from "../common/pageStyles";
import RecipientsCell, {
  parseRecipients,
} from "../Form/MwNowyHandling/RecipientsCell";
import {
  buildCompanyDict,
  ILOSC_PACZKI_FIELDS,
  ILOSC_PALETY_FIELDS,
  iloscGroupActive,
} from "../Form/MwNowyHandling/schema";

const BASE_URL = API_BASE_URL;
// Musi byc zgodne z DOPISZ_DANE_STORAGE_KEY w NowyHandling.jsx.
const SNAPSHOT_KEY = "mwNowyHandlingDopiszDane";
const EDITS_KEY = "mwNowyHandlingDopiszDaneEdits";

const KIERUNEK_OPTIONS = ["GB", "CH", "NO", "BA", "RS", "Lotniczy"];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Statusy, po ktorych etap odprawy celnej jest juz za nami (albo nie byl potrzebny).
// Widok dashboardu nie zwraca pol sekcji "Odprawa celna", wiec kompletnosc odprawy
// wnioskujemy ze statusu - to zgodne z logika przejsc w DetailPage.
const ODPRAWA_DONE_STATUSES = ["Oclono", "Towar do wydania", "Zrealizowano"];

// "HH:mm" -> "YYYY-MM-DD HH:mm" (data = dzis, strefa Warszawy) - tak jak w DetailPage.
const dateStamp = (hhmm) =>
  `${moment().tz("Europe/Warsaw").format("YYYY-MM-DD")} ${hhmm}`;

// "2026-09-10 14:30" / "14:30:00" -> "14:30"; brak dopasowania -> "".
const timePart = (value) => {
  const m = String(value ?? "").match(/(\d{2}:\d{2})(?::\d{2})?$/);
  return m ? m[1] : "";
};

// Godzina wklejona z arkusza: "8:30" -> "08:30", "08:30:00" -> "08:30".
const normalizeTime = (raw) => {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : s;
};

const normalizeYesNo = (raw) => {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "tak") return "Tak";
  if (s === "nie") return "Nie";
  return String(raw ?? "").trim();
};

const normalizeKierunek = (raw) => {
  const s = String(raw ?? "").trim();
  const hit = KIERUNEK_OPTIONS.find((o) => o.toLowerCase() === s.toLowerCase());
  return hit || s;
};

// Kolejnosc = kolejnosc kolumn przy wklejaniu z Excela.
const FIELDS = [
  {
    key: "kierunek",
    label: "Kierunek",
    kind: "enum",
    options: KIERUNEK_OPTIONS,
    paste: normalizeKierunek,
    width: 120,
  },
  { key: "iloscDeklPaczek", label: "Il. dekl. paczek", kind: "int", width: 110 },
  { key: "iloscFaktPaczek", label: "Il. fakt. paczek", kind: "int", width: 110 },
  { key: "iloscDeklPalet", label: "Il. dekl. palet", kind: "int", width: 110 },
  { key: "iloscFaktPalet", label: "Il. fakt. palet", kind: "int", width: 110 },
  {
    key: "problemZPrzesylka",
    label: "Problem z przesyłką",
    kind: "enum",
    options: ["Tak", "Nie"],
    paste: normalizeYesNo,
    width: 130,
  },
  {
    key: "zakonczenieSkanowania",
    label: "Zakończenie skanowania",
    kind: "time",
    paste: normalizeTime,
    width: 140,
  },
  {
    key: "towarCelny",
    label: "Towar celny",
    kind: "enum",
    options: ["Tak", "Nie"],
    paste: normalizeYesNo,
    width: 110,
  },
  {
    key: "podjazdAuta",
    label: "Podjazd auta",
    kind: "time",
    paste: normalizeTime,
    width: 130,
  },
  {
    key: "wyjazdAuta",
    label: "Godzina wyjazdu auta",
    kind: "time",
    paste: normalizeTime,
    width: 140,
  },
];

const PASTE_ORDER_HINT = FIELDS.map((f) => f.label).join("  ·  ");

// Pelniejsze naglowki do pobieralnego szablonu (skrocone label z FIELDS sluzy do
// waskich kolumn tabeli). Kolejnosc bierzemy z FIELDS, wiec nie moze sie rozjechac
// z parserem wklejania (handleConvert nizej).
const DOPISZ_DANE_TEMPLATE_HEADERS = {
  kierunek: "Kierunek",
  iloscDeklPaczek: "Ilość dekl. paczek",
  iloscFaktPaczek: "Ilość fakt. paczek",
  iloscDeklPalet: "Ilość dekl. palet",
  iloscFaktPalet: "Ilość fakt. palet",
  problemZPrzesylka: "Problem z przesyłką (Tak/Nie)",
  zakonczenieSkanowania: "Zakończenie skanowania (format: 13:30)",
  towarCelny: "Towar celny (Tak/Nie)",
  podjazdAuta: "Podjazd auta (format: 13:45)",
  wyjazdAuta: "Godzina wyjazdu auta (format: 14:00)",
};

// Pobranie pustego szablonu Excel do uzupelnienia i wklejenia z powrotem w pole
// "Wklej dane z Excela" (ta sama kolejnosc kolumn co FIELDS / handleConvert).
const handleDownloadDopiszDaneTemplate = () => {
  const headers = FIELDS.map(
    (field) => DOPISZ_DANE_TEMPLATE_HEADERS[field.key] || field.label,
  );
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  worksheet["!cols"] = headers.map(() => ({ wch: 26 }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Dopisz dane");
  XLSX.writeFile(workbook, "Magazyn_Warszawa_Dopisz_dane_szablon.xlsx");
};

const readSnapshot = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(SNAPSHOT_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch (error) {
    console.error("Nie udało się wczytać zaznaczonych zgłoszeń:", error);
    return [];
  }
};

// Paczki i palety wykluczaja sie: jesli aktywna jest jedna grupa (> 0), druga
// jest zerowana. Zwraca ten sam obiekt (mutuje).
const reconcileIlosc = (row) => {
  if (iloscGroupActive(ILOSC_PACZKI_FIELDS.map((f) => row[f]))) {
    ILOSC_PALETY_FIELDS.forEach((f) => {
      row[f] = "0";
    });
  } else if (iloscGroupActive(ILOSC_PALETY_FIELDS.map((f) => row[f]))) {
    ILOSC_PACZKI_FIELDS.forEach((f) => {
      row[f] = "0";
    });
  }
  return row;
};

const makeEdit = (base) =>
  reconcileIlosc({
    kierunek: base.kierunek ?? "",
    iloscDeklPaczek:
      base.iloscDeklPaczek == null ? "" : String(base.iloscDeklPaczek),
    iloscFaktPaczek:
      base.iloscFaktPaczek == null ? "" : String(base.iloscFaktPaczek),
    iloscDeklPalet:
      base.iloscDeklPalet == null ? "" : String(base.iloscDeklPalet),
    iloscFaktPalet:
      base.iloscFaktPalet == null ? "" : String(base.iloscFaktPalet),
    problemZPrzesylka: base.problemZPrzesylka ?? "",
    adresatProblemu: base.adresatProblemu ?? "",
    opisProblemu: base.opisProblemu ?? "",
    przeniesDoZwrotow: "",
    zakonczenieSkanowania: normalizeTime(base.zakonczenieSkanowania),
    towarCelny: base.towarCelny ?? "",
    podjazdAuta: normalizeTime(timePart(base.podjazdAuta) || base.podjazdAuta),
    wyjazdAuta: normalizeTime(timePart(base.wyjazdAuta) || base.wyjazdAuta),
  });

const fingerprint = (rows) => rows.map((r) => r.id).join(",");

const useStyles = makeStyles((theme) => ({
  pasteCard: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  pasteRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  hint: { fontSize: 13, color: COLORS.textMuted, marginTop: theme.spacing(1) },
  tableWrap: { overflowX: "auto" },
  footer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    flexWrap: "wrap",
    borderTop: `1px solid ${COLORS.border}`,
  },
  problemBox: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    padding: theme.spacing(1, 0),
  },
}));

const DopiszDane = () => {
  const classes = { ...usePageStyles(), ...useStyles() };
  const navigate = useNavigate();

  const [baseRows, setBaseRows] = useState(() => readSnapshot());
  const fp = useMemo(() => fingerprint(baseRows), [baseRows]);
  const [isSaving, setIsSaving] = useState(false);

  const [edits, setEdits] = useState(() => {
    const rows = readSnapshot();
    try {
      const saved = JSON.parse(localStorage.getItem(EDITS_KEY));
      if (
        saved &&
        saved.fp === fingerprint(rows) &&
        Array.isArray(saved.edits) &&
        saved.edits.length === rows.length
      ) {
        return saved.edits;
      }
    } catch (error) {
      console.error("Nie udało się wczytać wpisanych danych:", error);
    }
    return rows.map(makeEdit);
  });

  const [pasteText, setPasteText] = useState("");
  const [adresaciList, setAdresaciList] = useState([]);
  const adresaciDict = useMemo(
    () => buildCompanyDict(adresaciList),
    [adresaciList],
  );

  useEffect(() => {
    try {
      localStorage.setItem(EDITS_KEY, JSON.stringify({ fp, edits }));
    } catch (error) {
      console.error("Nie udało się zapisać wpisanych danych:", error);
    }
  }, [edits, fp]);

  useEffect(() => {
    const loadAdresaci = async () => {
      try {
        const { data } = await axios.get(
          `${BASE_URL}/list/adresaciProblemPrzesylki`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        const list = (data || [])
          .map((item) => item.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }));
        setAdresaciList(list);
      } catch (error) {
        console.error("Błąd przy pobieraniu listy adresatów:", error);
      }
    };
    loadAdresaci();
  }, []);

  const handleAddAdresat = useCallback(async (rawName) => {
    const name = String(rawName || "").trim();
    if (!name) throw new Error("Pusty adresat");
    await axios.post(
      `${BASE_URL}/add-record/adresaciProblemPrzesylki`,
      { name },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    setAdresaciList((prev) =>
      prev.some((a) => a.toLowerCase() === name.toLowerCase())
        ? prev
        : [...prev, name].sort((a, b) =>
            a.localeCompare(b, "pl", { sensitivity: "base" }),
          ),
    );
    return name;
  }, []);

  const setCell = useCallback((rowIdx, key, value) => {
    setEdits((prev) =>
      prev.map((e, i) => (i === rowIdx ? { ...e, [key]: value } : e)),
    );
  }, []);

  // Ustawienie pola "ilosc": wpisanie wartosci > 0 w grupie paczek zeruje palety
  // (i odwrotnie).
  const setIloscCell = useCallback((rowIdx, key, value) => {
    setEdits((prev) =>
      prev.map((e, i) => {
        if (i !== rowIdx) return e;
        const row = { ...e, [key]: value };
        const isPaczki = ILOSC_PACZKI_FIELDS.includes(key);
        const thisGroup = isPaczki ? ILOSC_PACZKI_FIELDS : ILOSC_PALETY_FIELDS;
        const otherGroup = isPaczki ? ILOSC_PALETY_FIELDS : ILOSC_PACZKI_FIELDS;
        if (iloscGroupActive(thisGroup.map((f) => row[f]))) {
          otherGroup.forEach((f) => {
            row[f] = "0";
          });
        }
        return row;
      }),
    );
  }, []);

  // Czy pole "ilosc" jest zablokowane (druga grupa aktywna, ta nie).
  const iloscLocked = (rowIdx, key) => {
    const row = edits[rowIdx] || {};
    const paczkiActive = iloscGroupActive(
      ILOSC_PACZKI_FIELDS.map((f) => row[f]),
    );
    const paletyActive = iloscGroupActive(
      ILOSC_PALETY_FIELDS.map((f) => row[f]),
    );
    if (ILOSC_PALETY_FIELDS.includes(key)) return paczkiActive && !paletyActive;
    if (ILOSC_PACZKI_FIELDS.includes(key)) return paletyActive && !paczkiActive;
    return false;
  };

  const handleConvert = () => {
    const lines = String(pasteText)
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .filter((line) => line.trim().length > 0);

    if (lines.length === 0) {
      toast.error("Wklej dane z arkusza.");
      return;
    }

    setEdits((prev) => {
      const next = prev.map((e) => ({ ...e }));
      lines.slice(0, next.length).forEach((line, i) => {
        const cells = line.split("\t");
        const kierunekLocked =
          String(baseRows[i]?.serwisLotniczy ?? "").trim() === "Tak";
        FIELDS.forEach((field, ci) => {
          const raw = cells[ci];
          if (raw === undefined) return;
          if (field.key === "kierunek" && kierunekLocked) return;
          next[i][field.key] = field.paste
            ? field.paste(raw)
            : String(raw).trim();
        });
        reconcileIlosc(next[i]);
      });
      return next;
    });

    if (lines.length > baseRows.length) {
      toast.warn(
        `Wklejono ${lines.length} wierszy, a zaznaczono ${baseRows.length}. Nadmiarowe pominięto.`,
      );
    } else {
      toast.success(
        `Uzupełniono ${Math.min(lines.length, baseRows.length)} z ${baseRows.length} wierszy.`,
      );
    }
  };

  const handleResetEdits = () => {
    setEdits(baseRows.map(makeEdit));
    setPasteText("");
  };

  // --- walidacja (jak w DetailPage) -----------------------------------------
  const errorsByCell = useMemo(() => {
    const out = {};
    edits.forEach((edit, i) => {
      const base = baseRows[i] || {};

      FIELDS.forEach((field) => {
        const value = String(edit[field.key] ?? "").trim();
        if (!value) return; // puste = nic nie dopisujemy
        if (field.kind === "enum" && !field.options.includes(value)) {
          out[`${i}:${field.key}`] = `Dozwolone: ${field.options.join(", ")}`;
        }
        if (field.kind === "int" && !/^\d+$/.test(value)) {
          out[`${i}:${field.key}`] = "Liczba całkowita ≥ 0";
        }
        if (field.kind === "time" && !TIME_RE.test(value)) {
          out[`${i}:${field.key}`] = "Godzina w formacie HH:MM";
        }
      });

      const start =
        timePart(base.startSkanowania) ||
        String(base.startSkanowania ?? "").trim();
      const zak = String(edit.zakonczenieSkanowania ?? "").trim();
      if (
        zak &&
        TIME_RE.test(zak) &&
        start &&
        TIME_RE.test(start) &&
        zak < start
      ) {
        out[`${i}:zakonczenieSkanowania`] =
          "Nie może być wcześniejsze niż start skanowania";
      }

      // Efektywny "Towar celny" = to, co wpisujemy teraz, a w braku tego -
      // to, co już jest w bazie. Steruje statusem (patrz statusForRow), wiec
      // "Podjazd auta" i "Godzina wyjazdu auta" musza byc z nim spojne.
      const effTowarCelny =
        String(edit.towarCelny ?? "").trim() ||
        String(base.towarCelny ?? "").trim();
      const odprawaZaNamiBase = ODPRAWA_DONE_STATUSES.includes(
        base.statusRealizacji,
      );

      const podjNowyWpis = String(edit.podjazdAuta ?? "").trim();
      const podjZmieniany =
        podjNowyWpis &&
        TIME_RE.test(podjNowyWpis) &&
        podjNowyWpis !== timePart(base.podjazdAuta);
      if (podjZmieniany && !effTowarCelny) {
        out[`${i}:podjazdAuta`] =
          'Uzupełnij najpierw „Towar celny”, aby wpisać „Podjazd auta”';
      }

      const podj =
        String(edit.podjazdAuta ?? "").trim() || timePart(base.podjazdAuta);
      const wyj = String(edit.wyjazdAuta ?? "").trim();
      if (wyj && TIME_RE.test(wyj)) {
        if (!podj) {
          out[`${i}:wyjazdAuta`] = "Najpierw uzupełnij „Podjazd auta”";
        } else if (TIME_RE.test(podj) && wyj < podj) {
          out[`${i}:wyjazdAuta`] =
            "Nie może być wcześniejsza niż podjazd auta";
        } else if (effTowarCelny === "Tak" && !odprawaZaNamiBase) {
          out[`${i}:wyjazdAuta`] =
            'Towar celny = Tak — dokończ najpierw odprawę celną w szczegółach zgłoszenia, zanim wpiszesz godzinę wyjazdu';
        }
      }

      // Adresat wymagany tylko gdy "Problem z przesyłką" jest NOWO ustawiany
      // na "Tak" w tej sesji (patrz problemNewlyTak w renderowaniu wiersza) -
      // jesli w bazie juz bylo "Tak", sekcja adresata jest ukryta i nie ma jej
      // gdzie uzupelnic.
      const problemBazowy = String(base.problemZPrzesylka ?? "").trim();
      const problemEdyt = String(edit.problemZPrzesylka ?? "").trim();
      if (
        problemEdyt === "Tak" &&
        problemBazowy !== "Tak" &&
        parseRecipients(edit.adresatProblemu).length === 0
      ) {
        out[`${i}:adresatProblemu`] =
          "Podaj co najmniej jednego adresata powiadomienia";
      }
    });
    return out;
  }, [edits, baseRows]);

  const errorCount = Object.keys(errorsByCell).length;
  const totalCols = 3 + FIELDS.length;

  // --- co dopisujemy do danego zgloszenia (tylko realnie zmienione pola) -----
  const changesForRow = useCallback(
    (i) => {
      const base = baseRows[i] || {};
      const edit = edits[i] || {};
      const changed = {};

      // Towar celny jako pierwsze - od niego zalezy status wyliczany w
      // statusForRow (rule A), wiec musi trafic do "changed" przed podjazdem.
      const towarCelny = String(edit.towarCelny ?? "").trim();
      if (towarCelny && towarCelny !== String(base.towarCelny ?? "").trim()) {
        changed.towarCelny = towarCelny;
      }

      if (
        edit.kierunek &&
        edit.kierunek !== String(base.kierunek ?? "").trim()
      ) {
        changed.kierunek = edit.kierunek;
      }

      // Paczki vs palety. Wpisanie w tej sesji wartosci > 0 w jednej grupie
      // zeruje druga (jak w DetailPage). "0" tam gdzie w bazie jest puste/null
      // nie liczymy jako zmiana.
      const groupNewlyActive = (fields) =>
        fields.some((k) => {
          const raw = String(edit[k] ?? "").trim();
          if (!/^\d+$/.test(raw) || Number(raw) <= 0) return false;
          const baseNum =
            base[k] == null || base[k] === "" ? null : Number(base[k]);
          return baseNum === null || Number(raw) !== baseNum;
        });
      const paczkiNewlyActive = groupNewlyActive(ILOSC_PACZKI_FIELDS);
      const paletyNewlyActive = groupNewlyActive(ILOSC_PALETY_FIELDS);

      const collectIlosc = (fields, otherNewlyActive) =>
        fields.forEach((key) => {
          if (otherNewlyActive) {
            changed[key] = 0;
            return;
          }
          const raw = String(edit[key] ?? "").trim();
          if (raw === "" || !/^\d+$/.test(raw)) return;
          const num = Number(raw);
          const baseNum =
            base[key] == null || base[key] === "" ? null : Number(base[key]);
          if (baseNum === null ? num !== 0 : num !== baseNum) {
            changed[key] = num;
          }
        });
      collectIlosc(ILOSC_PACZKI_FIELDS, paletyNewlyActive);
      collectIlosc(ILOSC_PALETY_FIELDS, paczkiNewlyActive);

      const zak = String(edit.zakonczenieSkanowania ?? "").trim();
      if (
        zak &&
        TIME_RE.test(zak) &&
        zak !== String(base.zakonczenieSkanowania ?? "").trim()
      ) {
        changed.zakonczenieSkanowania = zak;
      }

      const podj = String(edit.podjazdAuta ?? "").trim();
      if (podj && TIME_RE.test(podj) && podj !== timePart(base.podjazdAuta)) {
        changed.podjazdAuta = dateStamp(podj);
      }
      const wyj = String(edit.wyjazdAuta ?? "").trim();
      if (wyj && TIME_RE.test(wyj) && wyj !== timePart(base.wyjazdAuta)) {
        changed.wyjazdAuta = dateStamp(wyj);
      }

      const problem = String(edit.problemZPrzesylka ?? "").trim();
      if (problem && problem !== String(base.problemZPrzesylka ?? "").trim()) {
        changed.problemZPrzesylka = problem;
      }
      if (problem === "Tak") {
        const adr = parseRecipients(edit.adresatProblemu).join(", ");
        const opis = String(edit.opisProblemu ?? "").trim();
        if (adr && adr !== String(base.adresatProblemu ?? "").trim()) {
          changed.adresatProblemu = adr;
        }
        if (opis && opis !== String(base.opisProblemu ?? "").trim()) {
          changed.opisProblemu = opis;
        }
        // "Czy przenieść do Zwroty i Błędy?" = Tak -> zmiana rodzajTransportu.
        if (
          edit.przeniesDoZwrotow === "Tak" &&
          base.rodzajTransportu !== "Magazyn Warszawa Zwroty i Błędy"
        ) {
          changed.rodzajTransportu = "Magazyn Warszawa Zwroty i Błędy";
          // "Ilość fakt. paczek" (Nowy Handling) -> "Liczba paczek" (Zwroty i Błędy).
          // Bierzemy wartosc efektywnie zapisywana w tym samym zapisie (jesli byla
          // wlasnie zmieniona), inaczej to co jest juz w bazie.
          changed.liczbaPaczek =
            changed.iloscFaktPaczek !== undefined
              ? changed.iloscFaktPaczek
              : base.iloscFaktPaczek;
        }
      }

      return changed;
    },
    [baseRows, edits],
  );

  // Status wyliczany po tych samych regulach co w DetailPage (handleEditSave),
  // w tej samej kolejnosci etapow (A -> B -> D; rule C - "Odprawa celna kompletna
  // -> Oclono" - nie dotyczy tego widoku, bo tu nie edytujemy pol tej sekcji):
  //  A) wpisano "Towar celny" = Tak          -> "W odprawie celnej"
  //  B) wpisano "Podjazd auta":
  //       Towar celny (efektywnie) = Tak i odprawa jeszcze nie za nami
  //           -> "W oczekiwaniu na clenie"
  //       w przeciwnym razie                 -> "Towar do wydania"
  //  D) wpisano "Godzina wyjazdu auta", ale TYLKO gdy status po A i B to juz
  //     "Towar do wydania" (czyli towar nie wymaga odprawy albo odprawa jest
  //     juz zamknieta) -> "Zrealizowano". W przeciwnym razie zapis jest
  //     zablokowany walidacja w errorsByCell (patrz effTowarCelny/odprawaZaNamiBase),
  //     wiec ta galaz nie powinna nigdy wykonac sie z niespojnymi danymi.
  const statusForRow = (base, changed) => {
    let status = base.statusRealizacji;

    if (changed.towarCelny === "Tak") {
      status = "W odprawie celnej";
    }

    const effTowarCelny = changed.towarCelny ?? base.towarCelny;
    const odprawaZaNami = ODPRAWA_DONE_STATUSES.includes(base.statusRealizacji);

    if (changed.podjazdAuta !== undefined) {
      status =
        effTowarCelny === "Tak" && !odprawaZaNami
          ? "W oczekiwaniu na clenie"
          : "Towar do wydania";
    }
    if (changed.wyjazdAuta !== undefined && status === "Towar do wydania") {
      status = "Zrealizowano";
    }
    return status;
  };

  const jobs = useMemo(
    () =>
      baseRows
        .map((row, i) => ({ row, i, changed: changesForRow(i) }))
        .filter((job) => Object.keys(job.changed).length > 0),
    [baseRows, changesForRow],
  );

  const canSave = errorCount === 0 && jobs.length > 0 && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);

    const token = localStorage.getItem("token");
    let username = "";
    try {
      username = jwtDecode(token)?.username || "";
    } catch (error) {
      console.error("Nieprawidłowy token:", error);
    }
    const authHeaders = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // Pre-flight: duplikat numeru listu dla wierszy przenoszonych do "Zwroty i Błędy"
    // (ta sama walidacja co przy tworzeniu wpisu w formularzu).
    const duplikatIds = new Set();
    const movingJobs = jobs.filter((job) => job.changed.rodzajTransportu);
    if (movingJobs.length > 0) {
      const checks = await Promise.all(
        movingJobs.map(async (job) => {
          try {
            const { data } = await axios.get(
              `${BASE_URL}/check-numer-listu-magazyn-warszawa-zwroty-i-bledy`,
              {
                params: {
                  numerListu: String(job.row.numerListu ?? "").trim(),
                  rodzajTransportu: "Magazyn Warszawa Zwroty i Błędy",
                },
                headers: authHeaders,
              },
            );
            return { job, isDuplicate: Boolean(data?.isDuplicate), error: "" };
          } catch (checkErr) {
            return {
              job,
              isDuplicate: false,
              error: checkErr.response?.data?.message || "błąd sprawdzania",
            };
          }
        }),
      );
      const dupRefs = checks
        .filter((c) => c.isDuplicate)
        .map((c) => c.job.row.nrRef);
      const errRefs = checks
        .filter((c) => c.error)
        .map((c) => `${c.job.row.nrRef} (${c.error})`);
      if (dupRefs.length > 0 || errRefs.length > 0) {
        const linie = [];
        if (dupRefs.length) {
          linie.push(
            `Duplikat numeru listu w „Zwroty i Błędy": ${dupRefs.join(", ")}`,
          );
        }
        if (errRefs.length) {
          linie.push(`Nie sprawdzono: ${errRefs.join(", ")}`);
        }
        const kontynuuj = window.confirm(
          `${linie.join("\n")}\n\nPrzenieść te zgłoszenia mimo to (duplikaty zostaną oznaczone)?`,
        );
        if (!kontynuuj) {
          setIsSaving(false);
          return;
        }
      }
      checks
        .filter((c) => c.isDuplicate)
        .forEach((c) => duplikatIds.add(c.job.row.id));
    }

    let saved = 0;
    let statusChanges = 0;
    let mails = 0;
    let movedToZwroty = 0;
    const failedRefs = [];
    const savedIds = new Set();

    for (const job of jobs) {
      const { row, i, changed } = job;
      const edit = edits[i] || {};
      const payload = { ...changed };
      // Przy przeniesieniu do "Zwroty i Błędy" nie ruszamy statusu Nowego Handlingu.
      if (!changed.rodzajTransportu) {
        const newStatus = statusForRow(row, changed);
        if (newStatus && newStatus !== row.statusRealizacji) {
          payload.statusRealizacji = newStatus;
          if (newStatus === "Zrealizowano") {
            payload.endDate = moment
              .tz("Europe/Warsaw")
              .format("YYYY-MM-DDTHH:mm:ss");
          }
        }
      }
      if (duplikatIds.has(row.id)) {
        payload.duplikat = 1;
      }

      try {
        await axios.put(`${BASE_URL}/update/${row.id}`, payload, {
          headers: { ...authHeaders, username },
        });
        saved += 1;
        savedIds.add(row.id);
        if (payload.statusRealizacji) statusChanges += 1;
        if (payload.rodzajTransportu) movedToZwroty += 1;

        const problemDoWyslania =
          edit.problemZPrzesylka === "Tak" &&
          (changed.problemZPrzesylka === "Tak" ||
            changed.adresatProblemu !== undefined ||
            changed.opisProblemu !== undefined) &&
          parseRecipients(edit.adresatProblemu).length > 0;
        if (problemDoWyslania) {
          try {
            await axios.post(
              `${BASE_URL}/send-problem-przesylki-notification`,
              { nrRef: row.nrRef, id: row.id },
              { headers: authHeaders },
            );
            mails += 1;
          } catch (mailErr) {
            console.error("Powiadomienie nie wyszło:", row.nrRef, mailErr);
          }
        }
      } catch (err) {
        console.error("Zapis nie powiódł się:", row.nrRef, err);
        failedRefs.push(row.nrRef);
      }
    }

    setIsSaving(false);

    if (failedRefs.length === 0) {
      toast.success(
        `Zapisano ${saved} zgłoszeń · zmiana statusu: ${statusChanges} · powiadomienia: ${mails}` +
          (movedToZwroty > 0
            ? ` · przeniesiono do Zwroty i Błędy: ${movedToZwroty}` +
              (duplikatIds.size > 0
                ? ` (duplikaty: ${duplikatIds.size})`
                : "")
            : "") +
          ".",
        { autoClose: 4000 },
      );
      [SNAPSHOT_KEY, EDITS_KEY, "mwNowyHandlingSelectedRows"].forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(error);
        }
      });
      setTimeout(() => navigate("/magazynWarszawaNowyHandling"), 800);
      return;
    }

    // Czesciowy blad - zostawiamy w widoku tylko wiersze, ktore sie nie zapisaly.
    toast.error(
      `Zapisano ${saved}. Nie udało się: ${failedRefs.join(", ")}. Spróbuj ponownie.`,
      { autoClose: 7000 },
    );
    const keepIdx = [];
    const remainingRows = baseRows.filter((r, idx) => {
      const keep = !savedIds.has(r.id);
      if (keep) keepIdx.push(idx);
      return keep;
    });
    const remainingEdits = keepIdx.map((idx) => edits[idx]);
    try {
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(remainingRows));
    } catch (error) {
      console.error(error);
    }
    setBaseRows(remainingRows);
    setEdits(remainingEdits);
  };

  const renderEditCell = (rowIdx, field) => {
    const err = errorsByCell[`${rowIdx}:${field.key}`];
    const value = edits[rowIdx]?.[field.key] ?? "";
    // "Serwis lotniczy" = Tak (z danych zgloszenia) -> "Kierunek" zablokowany.
    const kierunekLocked =
      field.key === "kierunek" &&
      String(baseRows[rowIdx]?.serwisLotniczy ?? "").trim() === "Tak";
    const common = {
      size: "small",
      error: Boolean(err),
      value,
      onChange: (event) => setCell(rowIdx, field.key, event.target.value),
      style: { width: field.width },
    };

    let control;
    if (field.kind === "enum") {
      control = (
        <TextField select {...common} disabled={kierunekLocked}>
          <MenuItem value="">—</MenuItem>
          {field.options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      );
    } else if (field.kind === "int") {
      const locked = iloscLocked(rowIdx, field.key);
      control = (
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            type="number"
            inputProps={{ min: 0 }}
            autoComplete="off"
            size="small"
            error={Boolean(err)}
            style={{ width: field.width }}
            disabled={locked}
            value={locked ? "0" : value}
            onChange={(event) =>
              setIloscCell(rowIdx, field.key, event.target.value)
            }
          />
          {!locked && value !== "" && (
            <IconButton
              size="small"
              aria-label={`wyczyść ${field.label}`}
              onClick={() => setIloscCell(rowIdx, field.key, "")}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      );
    } else {
      control = (
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            type="time"
            InputLabelProps={{ shrink: true }}
            autoComplete="off"
            {...common}
          />
          {value !== "" && (
            <IconButton
              size="small"
              aria-label={`wyczyść ${field.label}`}
              onClick={() => setCell(rowIdx, field.key, "")}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      );
    }

    const tip =
      err ||
      (kierunekLocked
        ? '„Serwis lotniczy" = Tak — kierunek nie jest wymagany (pole zablokowane).'
        : "");
    return tip ? (
      <Tooltip arrow title={tip}>
        <span>{control}</span>
      </Tooltip>
    ) : (
      control
    );
  };

  const header = (
    <div className={classes.pageHeader}>
      <div>
        <h1 className={classes.title}>Dopisz dane</h1>
        <div className={classes.subtitle}>
          Magazyn Warszawa · zaznaczone zgłoszenia:{" "}
          <strong style={{ color: "#212121" }}>{baseRows.length}</strong>
        </div>
      </div>
      <div className={classes.headerActions}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/magazynWarszawaNowyHandling")}
        >
          Powrót do tabeli
        </Button>
      </div>
    </div>
  );

  if (baseRows.length === 0) {
    return (
      <>
        {header}
        <Alert severity="info">
          Nie wybrano żadnych zgłoszeń. Wróć do tabeli, zaznacz wiersze i kliknij „Dopisz dane".
        </Alert>
      </>
    );
  }

  return (
    <>
      {header}

        <div className={`${classes.card} ${classes.pasteCard}`}>
          <div className={classes.pasteRow}>
            <TextField
              label="Wklej dane z Excela (kolumny rozdzielone tabulatorem)"
              multiline
              minRows={3}
              fullWidth
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={handleDownloadDopiszDaneTemplate}
              >
                Pobierz szablon
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConvert}
              >
                Konwertuj do tabeli
              </Button>
              <Button variant="outlined" onClick={handleResetEdits}>
                Wyczyść
              </Button>
            </div>
          </div>
          <div className={classes.hint}>
            Kolejność kolumn: {PASTE_ORDER_HINT}. „Problem z przesyłką" = Tak/Nie
            — adresata i opis uzupełnia się w rozwinięciu wiersza. Wiersze
            wklejane są przypisywane po kolei do zaznaczonych zgłoszeń.
          </div>
        </div>

        {errorCount > 0 && (
          <Alert severity="warning" style={{ marginBottom: 12 }}>
            Błędy walidacji: <strong>{errorCount}</strong>. Popraw zaznaczone na
            czerwono pola.
          </Alert>
        )}

        <div className={classes.card}>
        <div className={classes.tableWrap}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell}>Nr Ref</TableCell>
                <TableCell className={classes.headCell}>Nr listu</TableCell>
                <TableCell className={classes.headCell}>Nazwa firmy</TableCell>
                {FIELDS.map((field) => (
                  <TableCell key={field.key} className={classes.headCell}>
                    {field.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {baseRows.map((row, i) => {
                // Sekcja adresata/opisu/przeniesienia ma sens tylko wtedy, gdy
                // "Problem z przesyłką" jest NOWO ustawiany na "Tak" w tej
                // sesji (puste/"Nie" -> "Tak"). Jesli w bazie juz jest "Tak",
                // problem zostal juz raz zgloszony - nie pokazujemy tych pol
                // ponownie (i nie wysylamy powiadomienia drugi raz).
                const problemNewlyTak =
                  String(edits[i]?.problemZPrzesylka).trim() === "Tak" &&
                  String(row.problemZPrzesylka ?? "").trim() !== "Tak";
                return (
                  <React.Fragment key={row.id ?? i}>
                    <TableRow hover={false} className={classes.row}>
                      <TableCell style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                        {row.nrRef}
                      </TableCell>
                      <TableCell>{row.numerListu}</TableCell>
                      <TableCell>{row.nadawca}</TableCell>
                      {FIELDS.map((field) => (
                        <TableCell key={field.key}>
                          {renderEditCell(i, field)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {problemNewlyTak && (
                      <TableRow>
                        <TableCell
                          colSpan={totalCols}
                          style={{ background: "#fafcff" }}
                        >
                          <div className={classes.problemBox}>
                            <div style={{ flex: "1 1 340px", minWidth: 300 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#666",
                                  marginBottom: 4,
                                }}
                              >
                                Adresat powiadomienia — {row.nrRef}
                              </div>
                              <RecipientsCell
                                value={edits[i]?.adresatProblemu ?? ""}
                                options={adresaciList}
                                dict={adresaciDict}
                                hasError={Boolean(
                                  errorsByCell[`${i}:adresatProblemu`],
                                )}
                                errorMessage={
                                  errorsByCell[`${i}:adresatProblemu`]
                                }
                                onChange={(v) =>
                                  setCell(i, "adresatProblemu", v)
                                }
                                onAddOption={handleAddAdresat}
                              />
                            </div>
                            <div style={{ flex: "2 1 420px", minWidth: 320 }}>
                              <TextField
                                label="Opis problemu (treść zostanie przesłana w wiadomości email)"
                                multiline
                                minRows={2}
                                fullWidth
                                size="small"
                                value={edits[i]?.opisProblemu ?? ""}
                                onChange={(event) =>
                                  setCell(i, "opisProblemu", event.target.value)
                                }
                              />
                            </div>
                            <div style={{ flex: "0 0 260px", minWidth: 240 }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Czy przenieść do Zwroty i Błędy?"
                                value={edits[i]?.przeniesDoZwrotow || "Nie"}
                                onChange={(event) =>
                                  setCell(
                                    i,
                                    "przeniesDoZwrotow",
                                    event.target.value,
                                  )
                                }
                              >
                                <MenuItem value="Nie">Nie</MenuItem>
                                <MenuItem value="Tak">Tak</MenuItem>
                              </TextField>
                              {edits[i]?.przeniesDoZwrotow === "Tak" && (
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: "#b26a00",
                                    marginTop: 4,
                                  }}
                                >
                                  Po zapisie zgłoszenie trafi do „Magazyn Warszawa
                                  Zwroty i Błędy".
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className={classes.footer}>
          <Button
            variant="contained"
            disableElevation
            disabled={!canSave}
            onClick={handleSave}
          >
            {isSaving
              ? "Zapisywanie…"
              : `Zapisz${
                  errorCount > 0
                    ? ` (błędy: ${errorCount})`
                    : jobs.length > 0
                      ? ` (${jobs.length})`
                      : ""
                }`}
          </Button>
          {(errorCount > 0 || jobs.length === 0) && (
            <Typography variant="body2" style={{ color: "#666" }}>
              {errorCount > 0
                ? "Popraw błędy, aby zapisać."
                : "Uzupełnij dane, aby było co zapisać."}
            </Typography>
          )}
        </div>
        </div>
    </>
  );
};

export default DopiszDane;
